import { Fragment, useState } from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { FIND_ONBOARDING_PAGE } from 'constants/pages';
import { type MarketplaceLinkParams, marketplaceUrl } from 'utils/shared/url/marketplaceUrl';
import { useModal } from 'hooks/useModal';
import { useRequireAuth } from 'hooks/useRequireAuth';
import MatchDetailsModal from 'screens/Find/components/MatchDetailsModal';
import { type FindFilters, countActiveFilters, makeDefaultFilters } from 'screens/Find/filters';
import type { FindGame } from 'screens/Find/types';
import BookCourtFeed, {
  useBookableFeedVenues,
} from 'screens/PlayerHomeV3/components/BookCourtFeed';
import {
  HomeDateRangeEnum,
  HomeFilters,
  HomeIntentEnum,
} from 'screens/PlayerHomeV3/components/GetOnCourtSection/types';
import Button, { ButtonLink } from 'components/Button';
import FiltersModal from 'components/Find/FiltersModal';
import {
  FiltersButton,
  ForYouPromptModal,
  ForYouSwitch,
  buildForYouFilters,
  useForYouScope,
} from 'components/Find/ForYou';
import Skeleton from 'components/LoadingSkeleton';
import FeaturedOpportunityCard from './FeaturedOpportunityCard';
import OpportunityRow from './OpportunityRow';
import { INTENT_LABEL, INTENT_MARKETPLACE_URL } from './intent';
import { chosenRadiusMiles, isVenueWhitelistActive } from './sheetFilters';
import { useTopOpportunities } from './useTopOpportunities';

const RequestToPlayModal = dynamic(
  () => import('screens/Find/components/RequestToPlay/RequestToPlayModal'),
);

interface Props {
  filters: HomeFilters;
  /** Lets the empty state widen the date range in place. */
  onFiltersChange?: (filters: HomeFilters) => void;
  /** The Filters sheet's committed values (shared with Players near you). */
  sheetFilters: FindFilters;
  onSheetFiltersChange: (filters: FindFilters) => void;
  /** Carries the bar's sport and picked neighborhood into the marketplace. */
  marketplaceParams?: MarketplaceLinkParams;
}

const WIDER_RANGE: Partial<Record<HomeDateRangeEnum, HomeDateRangeEnum>> = {
  [HomeDateRangeEnum.Today]: HomeDateRangeEnum.ThisWeek,
  [HomeDateRangeEnum.ThisWeek]: HomeDateRangeEnum.ThisMonth,
};

const RANGE_LABEL: Record<HomeDateRangeEnum, string> = {
  [HomeDateRangeEnum.Today]: 'today',
  [HomeDateRangeEnum.ThisWeek]: 'this week',
  [HomeDateRangeEnum.ThisMonth]: 'this month',
};

export default function TopOpportunitiesSection({
  filters,
  onFiltersChange,
  sheetFilters,
  onSheetFiltersChange,
  marketplaceParams,
}: Props) {
  const router = useRouter();
  const sheetModal = useModal();
  const { requireAuth, authModals, isAuthModalOpen } = useRequireAuth();
  // "For you" = the sheet pre-filled from the player's onboarding answers; "All" = sheet defaults.
  const { scope, changeScope, preferences, promptModal } = useForYouScope({
    sport: filters.sport,
    onApply: (prefs) =>
      onSheetFiltersChange(prefs ? buildForYouFilters(prefs, 'home') : makeDefaultFilters('home')),
    requireAuth,
  });
  const activeSheetCount = countActiveFilters(sheetFilters, 'home');
  const desktopSheetCount = countActiveFilters(sheetFilters, 'home', { excludeLocation: true });

  const { featured, rows, loading, isEmpty } = useTopOpportunities(filters, sheetFilters);
  // Court booking has its own feed shape (a day strip over clubs and their open
  // times) rather than the game/event rows, so it replaces the body below.
  const { venues: bookableVenues, loading: bookableLoading } = useBookableFeedVenues(
    filters,
    sheetFilters,
  );
  const isBookCourt = filters.intent === HomeIntentEnum.BookCourt;
  const widerRange = isBookCourt ? undefined : WIDER_RANGE[filters.dateRange];

  const gameDetailModal = useModal();
  const requestModal = useModal();
  const [detailGame, setDetailGame] = useState<FindGame | null>(null);
  // Captured before auth: an anonymous viewer logs in first and the feed refetches,
  // so the request modal reads the captured game, not the live one.
  const [requestGame, setRequestGame] = useState<FindGame | null>(null);

  const handleGameClick = (game: FindGame) => {
    setDetailGame(game);
    gameDetailModal.openModal();
  };

  const closeGameDetail = () => {
    gameDetailModal.closeModal();
    setDetailGame(null);
  };

  return (
    // Below lg the scope switch + filters lift out of the heading row into their own
    // divider-bracketed bar that reads as the tail of the search block, so the negative
    // top margin pulls the first divider back to 16px under the date chips.
    <section className="container mx-auto max-sm:-mt-ds-lg sm:max-lg:-mt-ds-2xl">
      <div className="flex items-center justify-between gap-ds-lg max-lg:flex-col max-lg:items-stretch">
        <h2 className="typography-informative-subheading-highlight text-color-text-lightmode-primary dark:text-color-text-darkmode-primary max-lg:order-2 lg:px-ds-xl">
          Top opportunities for you
        </h2>
        <div className="flex items-center gap-ds-lg max-lg:order-1 max-lg:justify-between max-lg:border-y max-lg:border-color-border-input-lightmode max-lg:py-ds-lg max-lg:dark:border-color-border-input-darkmode">
          <ForYouSwitch scope={scope} onScopeChange={changeScope} />
          <FiltersButton
            onClick={() => sheetModal.openModal()}
            activeCount={activeSheetCount}
            desktopActiveCount={desktopSheetCount}
          />
        </div>
      </div>

      <div className="flex flex-col gap-ds-xl max-lg:mt-ds-md max-lg:gap-ds-lg lg:mt-ds-xl">
        {isBookCourt ? (
          <BookCourtFeed venues={bookableVenues} loading={bookableLoading} />
        ) : loading ? (
          <>
            <Skeleton height="24rem" borderRadius="1.5rem" />
            <Skeleton height="12.5rem" borderRadius="1rem" />
            <Skeleton height="12.5rem" borderRadius="1rem" />
          </>
        ) : isEmpty ? (
          // @todo empty-state design pending; copy + actions per DECISIONS-HOME-FEED.md §2
          <div className="flex flex-col items-center gap-ds-lg py-ds-2xl text-center">
            <p className="typography-product-text-card-desktop text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
              No {INTENT_LABEL[filters.intent]} opportunities{' '}
              {sheetFilters.neighborhoods.length > 0
                ? 'in your selected neighborhoods'
                : isVenueWhitelistActive(sheetFilters)
                ? 'at your selected venues'
                : `within ${chosenRadiusMiles(sheetFilters)} mi`}{' '}
              {RANGE_LABEL[filters.dateRange]}.
            </p>
            <div className="flex items-center gap-ds-md">
              {widerRange && onFiltersChange && (
                <Button
                  variant="secondary"
                  size="md"
                  isInline
                  onClick={() => onFiltersChange({ ...filters, dateRange: widerRange })}
                >
                  Show {RANGE_LABEL[widerRange]}
                </Button>
              )}
              <ButtonLink
                href={marketplaceUrl(INTENT_MARKETPLACE_URL[filters.intent], marketplaceParams)}
                variant="primary"
                size="md"
                isInline
              >
                Browse all
              </ButtonLink>
            </div>
          </div>
        ) : (
          <>
            {featured && (
              <div className="max-lg:mb-ds-lg">
                <FeaturedOpportunityCard event={featured} />
              </div>
            )}
            {rows.map((item) => (
              <Fragment
                key={`${item.kind}-${item.kind === 'event' ? item.event.id : item.game.id}`}
              >
                <OpportunityRow item={item} onGameClick={handleGameClick} />
                <div className="h-px w-full bg-color-border-input-lightmode last:hidden dark:bg-color-border-input-darkmode" />
              </Fragment>
            ))}
          </>
        )}
      </div>

      <ForYouPromptModal isOpen={promptModal.isOpen} onClose={() => promptModal.closeModal()} />
      {authModals}

      <FiltersModal
        variant="home"
        isOpen={sheetModal.isOpen}
        onClose={() => sheetModal.closeModal()}
        filters={sheetFilters}
        onApply={onSheetFiltersChange}
        isTodayMode={filters.dateRange === HomeDateRangeEnum.Today}
        venueOptions={preferences?.venues}
        onReopen={() => sheetModal.openModal()}
        preferencesNotice={
          scope === 'forYou'
            ? { onViewPreferences: () => router.push(FIND_ONBOARDING_PAGE) }
            : undefined
        }
      />

      {/* One modal at a time: the detail modal hides while the request is up and
          reopens on "Go back". */}
      <MatchDetailsModal
        game={detailGame}
        isOpen={gameDetailModal.isOpen && !requestModal.isOpen && !isAuthModalOpen}
        onClose={closeGameDetail}
        onRequestToPlay={() => {
          setRequestGame(detailGame);
          requireAuth(requestModal.openModal);
        }}
      />

      <RequestToPlayModal
        game={requestGame}
        isOpen={requestModal.isOpen}
        onBack={requestModal.closeModal}
        onClose={() => {
          requestModal.closeModal();
          closeGameDetail();
        }}
      />
    </section>
  );
}
